import React, { useState, useRef, useEffect, useCallback } from 'react';
import h337 from 'heatmap.js';
import Utils from '../../utils/utils';
import axios from 'axios';
import JSZip from 'jszip';
import Pop from '../../Component/pop';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

// 注册 Chart.js 组件，包括 BarController
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend
);

// 视频时间分段数组，定义在组件外部避免重复创建
const timeArray = [
    1200, 4010, 5290, 8140, 10100, 12180, 14250, 17220, 19270, 22120,
    24180, 26160, 29080, 31170, 33240, 36200, 39000, 41060, 43030, 45100, 46230,
    49030, 50240, 53070, 54180, 56260, 58100
];

const Autism = (props) => {
    const heatmapContainer = useRef(null);
    const canvasRef = useRef(null);
    const points = useRef([]);
    const [showPop, setShowPop] = useState(false);
    const [popInfo, setPopInfo] = useState(['title', 'message', 'botton']); // 0: none, 1: success, 2: error
    const apiURL = process.env.REACT_APP_API_URL + '/webgazer/upload.php'
    const heatmapInstanceRef = useRef(null);
    const [showHeatmap, setShowHeatmap] = useState(false);
    const picIndexRef = useRef(1);
    const zipRef = useRef(null);
    const videoRef = useRef(null);
    const initGameRef = useRef(false);
    const [isStart, setStart] = useState(false);
    
    // 添加视频预加载相关状态
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [videoLoaded, setVideoLoaded] = useState(false);

    // 获取当前视频时间所属的分段
    const getCurrentVideoSegment = useCallback(() => {
        if (!videoRef.current) return 0;

        const currentTime = videoRef.current.currentTime * 1000; // 转换为毫秒

        // 找到当前时间所属的分段
        for (let i = 0; i < timeArray.length; i++) {
            if (currentTime < timeArray[i]) {
                return i + 1; // 返回分段号（从1开始）
            }
        }

        return timeArray.length + 1; // 最后一个分段
    }, []);

    // 预加载视频函数
    const preloadVideo = useCallback(async () => {
        return new Promise((resolve, reject) => {
            const videoPath = `${process.env.PUBLIC_URL}/video/video1.mp4`;
            const video = videoRef.current;
            
            if (!video) {
                reject(new Error('Video element not found'));
                return;
            }

            setIsVideoLoading(true);
            setLoadingProgress(0);

            // 监听加载进度
            const handleProgress = () => {
                if (video.buffered.length > 0) {
                    const bufferedEnd = video.buffered.end(video.buffered.length - 1);
                    const duration = video.duration;
                    if (duration > 0) {
                        const progress = Math.floor((bufferedEnd / duration) * 100);
                        setLoadingProgress(progress);
                    }
                }
            };

            // 监听可以播放事件
            const handleCanPlayThrough = () => {
                setIsVideoLoading(false);
                setVideoLoaded(true);
                setLoadingProgress(100);
                
                video.removeEventListener('progress', handleProgress);
                video.removeEventListener('canplaythrough', handleCanPlayThrough);
                video.removeEventListener('error', handleError);
                
                resolve();
            };

            // 监听错误事件
            const handleError = (error) => {
                setIsVideoLoading(false);
                video.removeEventListener('progress', handleProgress);
                video.removeEventListener('canplaythrough', handleCanPlayThrough);
                video.removeEventListener('error', handleError);
                reject(error);
            };

            // 绑定事件监听器
            video.addEventListener('progress', handleProgress);
            video.addEventListener('canplaythrough', handleCanPlayThrough);
            video.addEventListener('error', handleError);

            // 设置视频源并开始预加载
            video.src = videoPath;
            video.preload = 'auto';
            video.load();
        });
    }, []);

    const initHeatmap = () => {
        zipRef.current = new JSZip()
        heatmapInstanceRef.current = h337.create({
            container: heatmapContainer.current,
            radius: 50,
            maxOpacity: 0.6,
            minOpacity: 0.1,
            blur: 0.75,
        })
    }

    const countScore = useCallback(() => {
        let leftScore = 0;
        let rightScore = 0;

        points.current.forEach(point => {
            if (point.x < window.innerWidth / 2) {
                leftScore += point.value;
            } else {
                rightScore += point.value;
            }
        });

        return { leftScore, rightScore };
    }, []);

    const sendZip = useCallback(async (score, level) => {
        console.log('sendZip');
        const timestamp = Date.now();
        // const csvData = ' ,Geometric Image,Social Image' +
        //     '\nFixation time,' + score +
        //     '\nlevel,' + level;
        const csvData =
            `,Geometric Image,Social Image
Fixation time %, ${score},${100 - score}
Results(2=highly abnormal tendency;1=abnormal tendency;0=normal), ${level},`;

        zipRef.current.file(`score.csv`, csvData);
        zipRef.current.generateAsync({ type: 'blob' }).then((zipBlob) => {
            const formData = new FormData();
            formData.append('heatmapZip', zipBlob, `heatmap_${timestamp}.zip`);
            const sessionUid = sessionStorage.getItem('uid');
            formData.append('uid', sessionUid);
            formData.append('score', score);
            formData.append('level', level);
            formData.append('type', 1);
            axios.post(apiURL, formData)
                .then((response) => {
                    console.log('Success:', response.data);
                })
                .catch((error) => {
                    console.error('Error:', error);
                    setPopInfo(['error', '上传失败', '返回'])
                });
        });
    }, [apiURL]);

    const getTestingRect = () => {
        if (videoRef.current) {
            const rect = videoRef.current.getBoundingClientRect();
            const videoAspectRatio = videoRef.current.videoWidth / videoRef.current.videoHeight;
            const elementAspectRatio = rect.width / rect.height;

            if (videoAspectRatio > elementAspectRatio) {
                // Video is wider than the container
                const actualHeight = rect.width / videoAspectRatio;
                const offsetY = (rect.height - actualHeight) / 2;
                return {
                    left: rect.left,
                    right: rect.right,
                    top: rect.top + offsetY,
                    bottom: rect.top + offsetY + actualHeight,
                };
            } else {
                // Video is taller than the container
                const actualWidth = rect.height * videoAspectRatio;
                const offsetX = (rect.width - actualWidth) / 2;
                return {
                    left: rect.left + offsetX,
                    right: rect.left + offsetX + actualWidth,
                    top: rect.top,
                    bottom: rect.bottom,
                };
            }
        }
        return null;
    };

    const filterPoints = useCallback(() => {
        const rect = getTestingRect()
        console.log('rect:', rect)
        if (rect) {
            return points.current.filter(point =>
                point.x >= rect.left &&
                point.x <= rect.right &&
                point.y >= rect.top &&
                point.y <= rect.bottom
            );
        } else {
            return points.current;
        }
    }, []);

    const generateBarChart = useCallback(async (pointsData) => {
        // 按分段计算每个 segment 的 leftScore 占比
        const segmentCount = timeArray.length + 1;
        const segmentData = Array.from({ length: segmentCount }, (_, i) => {
            const segment = i + 1;
            let leftScore = 0;
            let rightScore = 0;

            // 筛选出当前分段的点
            const segmentPoints = pointsData.filter(point => point.segment === segment);

            // 计算左右分数
            segmentPoints.forEach(point => {
                if (point.x < window.innerWidth / 2) {
                    leftScore += point.value;
                } else {
                    rightScore += point.value;
                }
            });

            // 计算左侧占比百分比
            const totalScore = leftScore + rightScore;
            if (totalScore > 0)
                if (leftScore === 0)
                    return 1;
                else
                    return Math.floor(leftScore * 100 / totalScore);
            else
                return 0;
        });

        // 创建一个临时的canvas元素来生成柱状图
        const chartCanvas = document.createElement('canvas');
        chartCanvas.width = 600;
        chartCanvas.height = 400;

        // 配置柱状图数据
        const chartData = {
            labels: Array.from({ length: segmentCount }, (_, i) => (i + 1).toString()),
            datasets: [{
                label: '注視時長分佈',
                data: segmentData,
                backgroundColor: Array(segmentCount).fill('rgba(54, 162, 235, 0.6)'),
                borderColor: Array(segmentCount).fill('rgba(54, 162, 235, 1)'),
                borderWidth: 1
            }]
        };

        // 配置柱状图选项
        const chartOptions = {
            responsive: false,
            animation: false,
            plugins: {
                title: {
                    display: true,
                    text: '注視時長分佈圖' // 图表标题
                },
                legend: {
                    display: false
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    max: 100,
                    title: {
                        display: true,
                        text: '注視社交影像的平均時長百分比(%)'
                    }
                },
                x: {
                    title: {
                        display: true,
                        text: '場景'
                    }
                }
            }
        };

        // 创建图表
        const chart = new ChartJS(chartCanvas, {
            type: 'bar',
            data: chartData,
            options: chartOptions
        });

        // 等待图表渲染完成
        await new Promise(resolve => setTimeout(resolve, 100));

        // 获取图表的数据URL
        const chartDataURL = chartCanvas.toDataURL('image/png');

        // 销毁图表释放内存
        chart.destroy();

        // 转换为blob并返回
        const response = await fetch(chartDataURL);
        const chartBlob = await response.blob();

        return chartBlob;
    }, []);


    const captureVideoFrame = useCallback(async () => {
        const video = videoRef.current;
        const canvas = document.createElement('canvas');
        canvas.width = video.offsetWidth;
        canvas.height = video.offsetHeight;
        const ctx = canvas.getContext('2d');
        const videoRatio = video.videoWidth / video.videoHeight;
        const canvasRatio = canvas.width / canvas.height;
        if (videoRatio > canvasRatio) {
            // 视频更宽，适应宽度，调整高度
            var drawWidth = canvas.width;
            var drawHeight = canvas.width / videoRatio;
            var drawX = 0;
            var drawY = (canvas.height - drawHeight) / 2;
        } else {
            // 视频更高，适应高度，调整宽度
            drawWidth = canvas.height * videoRatio;
            drawHeight = canvas.height;
            drawX = (canvas.width - drawWidth) / 2;
            drawY = 0;
        }
        ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);

        const csvData = 'x,y,val,segment\n' + points.current.map(point => `${parseInt(100 * point.x / canvas.width)},${parseInt(100 * point.y / canvas.height)},${point.value},${point.segment || 0}`).join('\n');
        points.current = filterPoints();
        const data = { max: 3, data: points.current };
        heatmapInstanceRef.current.setData(data);
        const heatmapCanvas = heatmapContainer.current.querySelector('canvas');
        ctx.drawImage(heatmapCanvas, 0, 0, heatmapCanvas.width, heatmapCanvas.height);

        const dataURL = canvas.toDataURL('image/png');

        // 将promise链转换为async/await模式
        try {
            const response = await fetch(dataURL);
            const imgBlob = await response.blob();
            const csvBlob = new Blob([csvData], { type: 'text/csv' });

            // 使用当前所有点数据生成柱状图
            const chartBlob = await generateBarChart(points.current);

            zipRef.current.file(`Data_${picIndexRef.current}.csv`, csvBlob);
            zipRef.current.file(`Img_${picIndexRef.current}.png`, imgBlob);
            zipRef.current.file(`Chart_${picIndexRef.current}.png`, chartBlob);
            picIndexRef.current += 1;
        } catch (error) {
            console.error('图像处理失败:', error);
        }
    }, [filterPoints, generateBarChart]);

    const startGame = useCallback(async () => {
        console.log('startGame');
        setStart(true);
        points.current = []
        picIndexRef.current = 1;

        if (videoRef.current) {
            console.log('videoRef.current', videoRef.current)
            videoRef.current.play();
        }

        await Utils.sleep(60000);

        videoRef.current.pause();

        const pointSize = points.current.length;
        await captureVideoFrame();
        const pointSizeAfter = points.current.length;
        if (pointSize<50 || pointSizeAfter < pointSize / 2) {
            console.log('pointSizeAfter', pointSizeAfter, pointSize);
            setShowPop(true);
            setPopInfo(['兒童行爲測試結果', '注視時間太短，請返回重做', '返回'])
        } else {
            let result = countScore();
            const score = Math.floor(result.rightScore * 100 / (result.leftScore + result.rightScore))
            const level = score > 69 ? 2 : score > 50 ? 1 : 0
            setShowHeatmap(true);
            setShowPop(true);
            setPopInfo(['兒童行爲測試結果', '計算中', ''])
            console.log('sendZip');
            const info = `測試結果:${score}<br/>孩子行爲${level === 0 ? '傾向正常' : level === 1 ? '傾向異常' : '明顯傾向異常'}表現`;
            setPopInfo(['兒童行爲測試結果 ', info, 'upload'])
            await sendZip(score, level);
            setPopInfo(['兒童行爲測試結果', info, '返回'])
        }
        // await Utils.sleep(3000);
        // setShowHeatmap(false);

        // await Utils.sleep(1000);
    }, [captureVideoFrame, sendZip, countScore]);

    const onBackSelect = () => {
        setShowPop(false);
        setShowHeatmap(false);
        props.onBackSelect();
    }

    useEffect(() => {
        if (initGameRef.current) return;
        initGameRef.current = true;

        const initGameLocal = async () => {
            console.log('initGame');
            if (videoRef.current) {
                // videoRef.current.muted = true; // 静音视频
                // videoRef.current.play().catch((error) => {
                //     console.error('视频播放失败:', error);
                // });
            }
            initHeatmap();
            
            try {
                // 预加载视频
                await preloadVideo();
                console.log('视频预加载完成');
                
                setTimeout(() => {
                    startGame();
                }, 3000);
            } catch (error) {
                console.error('视频预加载失败:', error);
                setPopInfo(['错误', '视频加载失败，请刷新页面重试', '返回']);
                setShowPop(true);
            }
        }

        initGameLocal();

        return () => {
            console.log('Component will unmount');
        };
    }, [startGame, preloadVideo]);
    useEffect(() => {
        let x = Math.floor(props.eyePoint[0] * window.innerWidth/100)
        let y = Math.floor(props.eyePoint[1] * window.innerHeight/100)
        const segment = getCurrentVideoSegment();

        const existingPoint = points.current.find(point => point.x === x && point.y === y && point.segment === segment);
        if (existingPoint) {
            existingPoint.value += 1;
        } else {
            points.current.push({ x: x, y: y, value: 1, radius: 50, segment: segment });
        }
    }, [props.eyePoint, getCurrentVideoSegment]);

    return (
        <div style={{ minHeight: '100vh', width: '100vw', position: 'relative' }}>
            {showPop ? <Pop onClose={onBackSelect} info={popInfo} ></Pop> : ''}

            {/* 视频加载进度条 */}
            {isVideoLoading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                    color: 'white'
                }}>
                    <div style={{
                        marginBottom: '20px',
                        fontSize: '18px',
                        textAlign: 'center'
                    }}>
                        正在加载视频，请稍候...
                    </div>
                    <div style={{
                        width: '300px',
                        height: '6px',
                        backgroundColor: '#333',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        marginBottom: '10px'
                    }}>
                        <div style={{
                            width: `${loadingProgress}%`,
                            height: '100%',
                            backgroundColor: '#4CAF50',
                            transition: 'width 0.3s ease'
                        }}></div>
                    </div>
                    <div style={{ fontSize: '14px' }}>
                        {loadingProgress}%
                    </div>
                </div>
            )}

            <div className="heatmap"
                ref={heatmapContainer}
                style={{
                    opacity: showHeatmap ? 0.9 : 0.01,
                    width: '100vw',
                    height: '100vh',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: 99,
                    pointerEvents: 'none',
                    background: 'transparent',
                }}
            ></div>
            <div
                style={{
                    width: '100vw',
                    height: '100vh',
                    position: 'absolute',
                    background: `url(${process.env.PUBLIC_URL + '/res/pic' + props.picIndex + '.jpg'}) no-repeat center center / cover`,
                    top: 0,
                    left: 0,
                }}>
            </div>
            <canvas ref={canvasRef} style={{
                display: 'none',
                width: '100vw',
                height: '100vh',
            }}></canvas>

            <video ref={videoRef} loop
                style={{
                    position: 'absolute',
                    top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '100vw',
                    height: '100vh',
                    zIndex: 1,
                    display: isStart ? 'block' : 'none',
                }} />
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: (videoLoaded && !isStart) ? 'block' : 'none',
            }}>
                <img src={`${process.env.PUBLIC_URL}/res/focus.png`} alt="Focus" />
            </div>



        </div>
    )
};

export default Autism;
